import React, { useContext } from 'react';

import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import { FiAlertCircle, FiCheck, FiMail, FiMaximize2, FiShield, FiX } from 'react-icons/fi';

import { createStaffInviteRequest } from '../../../../api/invites';
import { ChatCard, ChatCardSection } from '../../../../components/Chat/kit';
import { LailaCardsContext } from './LailaCardsContext';
import { ADMIN_INVITE_FORM, adminInvitePrefillFrom, useOpenPage } from './fullForm';
import { adminInvitePrefill } from '../../../staff-management/prefill';
import type { LailaCardViewProps } from './LailaCard';
import { inviteErrorMessage } from './inviteDraft';
import {
  StaffInviteChange,
  StaffInviteData,
  StaffModule,
  applyStaffChange,
  hasOwnerRole,
  roleOf,
  scopeNoun,
  staffAccess,
  staffPayload,
  staffProblems,
} from './staffInvite';

// An admin or manager invite Laila drafted (the staff_invite_draft card): the
// user checks the email, position and each module's role, and sends it from
// here with this page's own call to the Vyce API.
export const StaffInviteCard: React.FC<LailaCardViewProps> = ({ card }) => {
  const classes = useStyles();
  const { updateCard, noteForLaila } = useContext(LailaCardsContext);
  const data = card.data as unknown as StaffInviteData;

  const save = (next: StaffInviteData) => updateCard(card.id, next as unknown as Record<string, unknown>);
  const change = (next: StaffInviteChange) => save(applyStaffChange(data, next));

  const send = async () => {
    if (staffProblems(data).length || data.state === 'sending') {
      return;
    }
    save({ ...data, state: 'sending', message: undefined });
    try {
      await createStaffInviteRequest(data.companyId, staffPayload(data));
      save({ ...data, state: 'started' });
      noteForLaila(
        `The user sent the admin invite from the card to ${data.email} as ${data.position} (${staffAccess(
          data
        )}).`
      );
    } catch (e) {
      save({ ...data, state: 'failed', message: inviteErrorMessage(e) });
    }
  };

  const cancel = () => {
    save({ ...data, state: 'cancelled' });
    noteForLaila('The user cancelled the admin invite draft, so nothing was sent.');
  };

  const openPage = useOpenPage();
  const openFullForm = () => {
    adminInvitePrefill.put(adminInvitePrefillFrom(data));
    noteForLaila('The user took the admin invite draft to the full Add Admin form, to finish it there.');
    openPage?.(ADMIN_INVITE_FORM);
  };

  if (data.state === 'cancelled') {
    return (
      <ChatCard
        label="Admin invite"
        icon={<FiX aria-hidden />}
        iconTone="neutral"
        title="Invite cancelled"
        subtitle={`${data.email} · nothing was sent`}
        footer={
          <Button color="primary" size="small" onClick={() => save({ ...data, state: undefined })}>
            Restore
          </Button>
        }
      />
    );
  }

  if (data.state === 'sending' || data.state === 'started') {
    const started = data.state === 'started';
    return (
      <ChatCard
        label="Admin invite"
        icon={started ? <FiCheck aria-hidden /> : <FiMail aria-hidden />}
        iconTone={started ? 'success' : 'brand'}
        title={started ? `Invite sent to ${data.email}` : `Inviting ${data.email}`}
        subtitle={data.position}
        badge={started ? 'Sent' : 'Sending'}
        badgeTone={started ? 'success' : 'neutral'}
      >
        <ChatCardSection>
          <Typography variant="body2">{staffAccess(data)}</Typography>
          <Typography variant="caption" color="textSecondary">
            {started ? 'Their access starts when they accept the email.' : 'Sending the invite…'}
          </Typography>
        </ChatCardSection>
      </ChatCard>
    );
  }

  const problems = staffProblems(data);
  const failed = data.state === 'failed';

  return (
    <ChatCard
      label="Admin invite draft"
      icon={<FiShield aria-hidden />}
      title="Invite an admin"
      badge={failed ? 'Not sent' : problems.length ? `${problems.length} to fix` : 'Draft'}
      badgeTone={failed ? 'error' : problems.length ? 'warning' : 'brand'}
      footer={
        <Box className={classes.footer}>
          <Box className={classes.buttons}>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              startIcon={<FiMail aria-hidden />}
              disabled={!!problems.length}
              onClick={send}
            >
              {failed ? 'Try again' : 'Send invite'}
            </Button>
            <Button onClick={cancel}>Cancel</Button>
            {openPage && (
              <Button color="primary" startIcon={<FiMaximize2 aria-hidden />} onClick={openFullForm}>
                Open full form
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {problems[0] || 'Nothing is sent until you tap Send. Their access starts when they accept.'}
          </Typography>
        </Box>
      }
    >
      {failed && (
        <ChatCardSection>
          <Box className={classes.alert} role="alert">
            <FiAlertCircle aria-hidden />
            <Typography variant="body2">{data.message}</Typography>
          </Box>
        </ChatCardSection>
      )}
      <ChatCardSection title="Who">
        <TextField
          id={`${card.id}-email`}
          label="Email"
          size="small"
          variant="outlined"
          fullWidth
          value={data.email}
          error={!data.emailValid}
          helperText={!data.emailValid ? 'Not an email address' : undefined}
          onChange={event => change({ type: 'email', email: event.target.value })}
        />
        <TextField
          id={`${card.id}-position`}
          label="Position"
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Site Manager"
          value={data.position}
          onChange={event => change({ type: 'position', position: event.target.value })}
          className={classes.field}
        />
      </ChatCardSection>
      <ChatCardSection title="Access">
        {data.modules.map(module => (
          <ModuleAccess key={module.name} module={module} onChange={change} />
        ))}
        {hasOwnerRole(data) && (
          <Typography variant="caption" color="textSecondary">
            Only an owner can make someone an owner.
          </Typography>
        )}
      </ChatCardSection>
    </ChatCard>
  );
};

// One module: its roles to pick from, and what a scoped role covers.
const ModuleAccess: React.FC<{ module: StaffModule; onChange: (change: StaffInviteChange) => void }> = ({
  module,
  onChange,
}) => {
  const classes = useStyles();
  const role = roleOf(module);
  const noun = scopeNoun(module);

  return (
    <Box className={classes.module}>
      <Typography variant="body2" className={classes.moduleName}>
        {module.name}
      </Typography>
      <Box className={classes.chips} role="group" aria-label={`Role in ${module.name}`}>
        {[{ id: null, name: 'No access' }, ...module.roles].map(option => (
          <Chip
            key={option.id ?? 'none'}
            label={option.name}
            size="small"
            clickable
            color={module.roleId === option.id ? 'primary' : 'default'}
            variant={module.roleId === option.id ? 'default' : 'outlined'}
            aria-pressed={module.roleId === option.id}
            onClick={() => onChange({ type: 'role', module: module.name, roleId: option.id })}
          />
        ))}
      </Box>
      {module.asked?.role && !role && (
        <Typography variant="caption" color="textSecondary">
          No “{module.asked.role}” role here. Pick one above.
        </Typography>
      )}
      {role?.scoped && (
        <Box className={classes.scope}>
          <Typography variant="caption" color="textSecondary" component="p">
            Which {noun}?
          </Typography>
          <Box className={classes.chips} role="group" aria-label={`${noun} for ${module.name}`}>
            {module.resources.map(resource => (
              <Chip
                key={resource.id}
                label={resource.name}
                size="small"
                clickable
                color={module.scope.includes(resource.id) ? 'primary' : 'default'}
                variant={module.scope.includes(resource.id) ? 'default' : 'outlined'}
                aria-pressed={module.scope.includes(resource.id)}
                onClick={() => onChange({ type: 'scope', module: module.name, id: resource.id })}
              />
            ))}
          </Box>
          {!!module.asked?.unmatchedScope?.length && (
            <Typography variant="caption" color="textSecondary">
              Not found: {module.asked.unmatchedScope.join(', ')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    field: { marginTop: 8 },
    module: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      padding: '8px 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-of-type': { borderBottom: 0 },
    },
    moduleName: { fontWeight: 600 },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
    scope: { display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12, paddingTop: 4 },
    footer: { display: 'flex', flexDirection: 'column', gap: 6, width: '100%' },
    buttons: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    alert: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 8,
      color: theme.palette.error.main,
      '& svg': { flex: 'none', marginTop: 3 },
    },
  })
);
